
import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { TenderAPI } from "../Services/Api";

interface Contractor {
  vendorNo: number;
  vendor: string;
}

interface TenderFormData {
  tender: string;
  tenderDate: string;
  bidDate: string;
  tenderType: string;
  contractDate: string;
  contractor: string;
}

const CreateTender: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const projectParam = searchParams.get("projectNo");
  const projectNo = projectParam ? Number(projectParam) : 0;


const sessionParam = searchParams.get("sessionNo");
const sessionNo = sessionParam ? Number(sessionParam) : 0;


  const [formData, setFormData] = useState<TenderFormData>({
    tender: "",
    tenderDate: "",
    bidDate: "",
    tenderType: "",
    contractDate: "",
    contractor: "",
  });

  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Load vendors whose AccountHead is 11.
  useEffect(() => {
    const loadContractors = async () => {
      try {
        setLoadingContractors(true);
        setError("");

        const data = await TenderAPI.getContractors();
        setContractors(data);
      } catch (err) {
        console.error("Failed to load contractors:", err);
        setError("Failed to load contractors. Please try again.");
      } finally {
        setLoadingContractors(false);
      }
    };

    loadContractors();
  }, []);

  // Handle form input changes.
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Create contract.
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    if (!projectNo || !Number.isInteger(projectNo) || projectNo <= 0) {
      setError(
        "Project number is missing. Please open Create Contract from a project."
      );
      return;
    }

  
if (!Number.isInteger(sessionNo) || sessionNo <= 0) {
  setError(
    "Session number is missing. Please return to Dashboard and select the project again."
  );
  return;
}


    if (!formData.tender.trim()) {
      setError("Tender / Contract Name is required.");
      return;
    }

    if (!formData.tenderType) {
      setError("Please select a Tender Type.");
      return;
    }

    if (!formData.contractor) {
      setError("Please select a Contractor.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        tender: formData.tender.trim(),
        tenderDate: formData.tenderDate || null,
        bidDate: formData.bidDate || null,
        tenderType: formData.tenderType,
        project: projectNo,
        workNode: 0,
        //lastSession: null,
        lastSession: sessionNo,
        changeType: "Insert",
        contractDate: formData.contractDate || null,
        contractor: Number(formData.contractor),
      };

      const response = await TenderAPI.create(payload);

      setSuccess(
        `Contract created successfully. Contract No: ${
          response.tenderNo ?? "Created"
        }`
      );

      setTimeout(() => {
        navigate(`/projects/${projectNo}/contracts`);
      }, 1200);
    } catch (err: unknown) {
      console.error("Create Contract Error:", err);

      const errorMessage =
        typeof err === "object" &&
        err !== null &&
        "response" in err
          ? (
              err as {
                response?: {
                  data?: { message?: string };
                };
              }
            ).response?.data?.message
          : undefined;

      setError(errorMessage || "Failed to create contract.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const inputClass =
    "w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

  const readOnlyClass =
    "w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-700";

  const labelClass =
    "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="min-h-[calc(100vh-80px)] bg-gray-100 px-6 py-6">
      <div className="max-w-5xl mx-auto">
        {/* Page header */}
        <div className="mb-5">
          <h2 className="text-2xl font-semibold text-gray-800">
            Create Contract
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            Enter tender and contract details.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-gray-200 rounded-lg shadow-sm"
        >
          <div className="p-6">
            <h3 className="text-base font-semibold text-gray-800 mb-4">
              Tender Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Tender name */}
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Tender / Contract Name
                  <span className="text-red-500 ml-1">*</span>
                </label>

                <input
                  type="text"
                  name="tender"
                  value={formData.tender}
                  onChange={handleChange}
                  placeholder="Enter tender / contract name"
                  className={inputClass}
                  required
                />
              </div>

              {/* Tender date */}
              <div>
                <label className={labelClass}>Tender Date</label>

                <input
                  type="date"
                  name="tenderDate"
                  value={formData.tenderDate}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              {/* Bid date */}
              <div>
                <label className={labelClass}>Bid Date</label>

                <input
                  type="date"
                  name="bidDate"
                  value={formData.bidDate}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              {/* Tender type */}
              <div>
                <label className={labelClass}>
                  Tender Type
                  <span className="text-red-500 ml-1">*</span>
                </label>

                <select
                  name="tenderType"
                  value={formData.tenderType}
                  onChange={handleChange}
                  className={inputClass}
                  required
                >
                  <option value="">Select Tender Type</option>
                  <option value="Billable">Billable</option>
                  <option value="Receivable">Receivable</option>
                </select>
              </div>

              {/* Project number */}
             {/*  <div>
                <label className={labelClass}>Project No</label>

                <input
                  type="number"
                  value={projectNo || ""}
                  readOnly
                  className={readOnlyClass}
                />
              </div> */}

              {/* Work node */}
              {/* <div>
                <label className={labelClass}>Work Node</label>

                <input
                  type="number"
                  value={0}
                  readOnly
                  className={readOnlyClass}
                />
              </div> */}

              {/* Last session */}
             {/*  <div>
                <label className={labelClass}>Last Session</label>

                <input
                  type="text"
                  value="To be determined"
                  readOnly
                  className={readOnlyClass}
                />
              </div> */}

              {/* Change type */}
              {/* <div>
                <label className={labelClass}>Change Type</label>

                <input
                  type="text"
                  value="Insert"
                  readOnly
                  className={readOnlyClass}
                />
              </div> */}

              {/* Contract date */}
              <div>
                <label className={labelClass}>Contract Date</label>

                <input
                  type="date"
                  name="contractDate"
                  value={formData.contractDate}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              {/* Contractor dropdown */}
              <div>
                <label className={labelClass}>
                  Contractor
                  <span className="text-red-500 ml-1">*</span>
                </label>

                <select
                  name="contractor"
                  value={formData.contractor}
                  onChange={handleChange}
                  className={inputClass}
                  required
                  disabled={loadingContractors}
                >
                  <option value="">
                    {loadingContractors
                      ? "Loading contractors..."
                      : "Select Contractor"}
                  </option>

                  {contractors.map((contractor) => (
                    <option
                      key={contractor.vendorNo}
                      value={contractor.vendorNo}
                    >
                      {contractor.vendor}
                    </option>
                  ))}
                </select>

                {!loadingContractors && contractors.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    No contractors found. Check the vendor API and AccountHead = 11.
                  </p>
                )}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="mt-5 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Success */}
            {success && (
              <div className="mt-5 px-4 py-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                {success}
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading || loadingContractors}
              className="px-5 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create Contract"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTender;
